import React, { useCallback, useEffect, useRef, useState } from "react";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";

import { normalizePhoneNumber } from "components/suno-dialer/utils";
import { useLazyQueryPatientsQuery } from "services/patients";
import { DEFAULT_PATIENTS_QUERY } from "services/patients";
import { type Patient } from "services/types/patients.type";

import { useNeonNow } from "./use-neon-now.hook";

interface IncomingCall {
  contactId: string;
  phone: string;
  timestamp: Date;
  isActive: boolean;
  agentLogin: string;
  queueName: string;
  contactEvent: string;
}

interface IncomingCallWidgetProps {
  onOpenPhoneWidget?: () => void;
  onNavigateToPatient?: (patientId: string) => void;
}

const IncomingCallWidget: React.FC<IncomingCallWidgetProps> = ({
  onOpenPhoneWidget,
  onNavigateToPatient
}) => {
  const { onEvent, handleOpen } = useNeonNow();
  const [currentCall, setCurrentCall] = useState<IncomingCall | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Drag functionality state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const widgetRef = useRef<HTMLDivElement>(null);

  const [queryPatients] = useLazyQueryPatientsQuery();

  // Drag event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!widgetRef.current) return;

    const rect = widgetRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Constrain to viewport bounds
      const maxX = window.innerWidth - 400; // widget width
      const maxY = window.innerHeight - 500; // approximate widget height

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    },
    [isDragging, dragOffset]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Function to fetch patient details by phone number
  const fetchPatientByPhone = useCallback(
    async (phoneNumber: string): Promise<Patient | null> => {
      setIsLoadingPatient(true);

      try {
        const normalizedPhone = normalizePhoneNumber(phoneNumber);
        const { data } = await queryPatients({
          search: normalizedPhone,
          baseFilter: {},
          is_active: true,
          limit: 1,
          query: DEFAULT_PATIENTS_QUERY
        });

        setIsLoadingPatient(false);
        return data?.length ? data[0] : null;
      } catch (error) {
        console.error("Failed to fetch patient details:", error);
        setIsLoadingPatient(false);
        return null;
      }
    },
    [queryPatients]
  );

  // Extract phone number from AWS Connect event connections
  const extractPhoneFromConnections = (connections: any[]): string => {
    const phoneConnection = connections.find(
      (conn) => conn.endpoint.type === "phone_number" && conn.type === "inbound"
    );
    return phoneConnection?.endpoint.phoneNumber || "Unknown Number";
  };

  // Handle AWS Connect events
  const handleConnectEvent = useCallback(
    async (payload: any) => {
      // Only handle incoming call connecting events
      if (
        payload.contactEvent === "contact::connecting" &&
        payload.contactSnapshot.initiationMethod === "inbound"
      ) {
        const phoneNumber = extractPhoneFromConnections(
          payload.contactSnapshot.connections
        );

        const incomingCall: IncomingCall = {
          contactId: payload.contactId,
          phone: phoneNumber,
          timestamp: new Date(payload.contactSnapshot.state.timestamp),
          isActive: true,
          agentLogin: payload.agentLogin,
          queueName: payload.contactSnapshot.queue.name,
          contactEvent: payload.contactEvent
        };

        setCurrentCall(incomingCall);
        setIsVisible(true);

        // Fetch patient details for the incoming call
        try {
          const patientData = await fetchPatientByPhone(phoneNumber);
          setPatient(patientData);
        } catch (error) {
          console.error("Failed to fetch patient details:", error);
          setPatient(null);
        }
      }

      // Handle call ended events to hide the widget
      if (
        payload.contactEvent === "contact::ended" &&
        currentCall?.contactId === payload.contactId
      ) {
        setIsVisible(false);
        setCurrentCall(null);
        setPatient(null);
      }
    },
    [currentCall?.contactId, fetchPatientByPhone]
  );

  // Subscribe to AWS Connect events
  useEffect(() => {
    const unsubscribe = onEvent(
      "CONNECT_CONTACT_STATE_CHANGE",
      handleConnectEvent
    );
    return unsubscribe;
  }, [onEvent, currentCall, handleConnectEvent]);

  // Handle view patient
  const handleViewPatient = () => {
    if (patient?.id && onNavigateToPatient) {
      onNavigateToPatient(String(patient.id));
    }
  };

  // Handle open phone widget
  const handleOpenPhoneWidget = () => {
    if (onOpenPhoneWidget) {
      onOpenPhoneWidget();
    } else {
      // Fallback to opening neon now drawer
      handleOpen();
    }
  };

  if (!isVisible || !currentCall) {
    return null;
  }

  return (
    <div
      className={`transition-all duration-300 ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
    >
      <div
        ref={widgetRef}
        style={{
          position: "fixed",
          top: `${position.y}px`,
          left: `${position.x}px`,
          zIndex: 9999,
          maxWidth: "400px",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          border: "2px solid #3b82f6",
          animation: "pulse-ring 2s infinite",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none"
        }}
      >
        <div style={{ padding: "24px" }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <DragIndicatorIcon
                sx={{
                  color: "#6b7280",
                  fontSize: 20,
                  cursor: "grab",
                  "&:active": { cursor: "grabbing" }
                }}
                onMouseDown={handleMouseDown}
              />
              <VolumeUpIcon sx={{ color: "#3b82f6", fontSize: 24 }} />
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#2563eb",
                  margin: 0
                }}
              >
                Incoming Call
              </h2>
            </div>
            <span
              style={{
                backgroundColor: "#ef4444",
                color: "white",
                fontSize: "12px",
                padding: "4px 8px",
                borderRadius: "9999px",
                fontWeight: "bold"
              }}
            >
              LIVE
            </span>
          </div>

          {/* Patient Info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "24px"
            }}
          >
            {isLoadingPatient ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    backgroundColor: "#e5e7eb",
                    borderRadius: "50%",
                    animation: "pulse 2s infinite"
                  }}
                ></div>
                <div>
                  <div
                    style={{
                      height: "16px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      width: "128px",
                      marginBottom: "8px",
                      animation: "pulse 2s infinite"
                    }}
                  ></div>
                  <div
                    style={{
                      height: "12px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      width: "96px",
                      animation: "pulse 2s infinite"
                    }}
                  ></div>
                </div>
              </div>
            ) : (
              <>
                <div style={{ position: "relative" }}>
                  {patient?.photo ? (
                    <img
                      src={patient.photo}
                      alt={`${patient.first_name} ${patient.last_name}`}
                      style={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "4px solid #dbeafe"
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "64px",
                        height: "64px",
                        backgroundColor: "#3b82f6",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <PersonIcon sx={{ color: "white", fontSize: 32 }} />
                    </div>
                  )}
                  <div
                    style={{
                      position: "absolute",
                      bottom: "-4px",
                      right: "-4px",
                      width: "16px",
                      height: "16px",
                      backgroundColor: "#10b981",
                      borderRadius: "50%",
                      border: "2px solid white"
                    }}
                  ></div>
                </div>
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#111827",
                      margin: 0,
                      marginBottom: "4px"
                    }}
                  >
                    {patient
                      ? `${patient.first_name} ${patient.last_name}`
                      : "Unknown Caller"}
                  </h3>
                  <p
                    style={{
                      color: "#6b7280",
                      fontSize: "14px",
                      margin: 0,
                      marginBottom: "4px"
                    }}
                  >
                    {currentCall.phone}
                  </p>
                  {patient?.previous_appointment_moment && (
                    <p
                      style={{
                        color: "#9ca3af",
                        fontSize: "12px",
                        margin: 0
                      }}
                    >
                      Last visit:{" "}
                      {new Date(
                        patient.previous_appointment_moment
                      ).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Patient Actions */}
          {patient && (
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
              <button
                onClick={handleViewPatient}
                style={{
                  flex: 1,
                  border: "1px solid #3b82f6",
                  color: "#3b82f6",
                  backgroundColor: "transparent",
                  fontWeight: "500",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = "#eff6ff")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <PersonIcon sx={{ fontSize: 18 }} />
                View Patient
              </button>
              <button
                onClick={handleOpenPhoneWidget}
                style={{
                  flex: 1,
                  border: "1px solid #3b82f6",
                  color: "#3b82f6",
                  backgroundColor: "transparent",
                  fontWeight: "500",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = "#eff6ff")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <PhoneIcon sx={{ fontSize: 18 }} />
                Phone Widget
              </button>
            </div>
          )}

          {/* Call Info */}
          <div style={{ paddingTop: "16px", borderTop: "1px solid #e5e7eb" }}>
            <p
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                margin: 0,
                marginBottom: "4px"
              }}
            >
              Contact ID: {currentCall.contactId}
            </p>
            <p
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                margin: 0,
                marginBottom: "4px"
              }}
            >
              Queue: {currentCall.queueName}
            </p>
            <p
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                margin: 0,
                marginBottom: "4px"
              }}
            >
              Agent: {currentCall.agentLogin}
            </p>
            <p style={{ fontSize: "12px", color: "#9ca3af", margin: 0 }}>
              Started: {currentCall.timestamp.toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes pulse-ring {
            0% {
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
            }
            50% {
              box-shadow: 0 0 0 15px rgba(59, 130, 246, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
    </div>
  );
};

export default IncomingCallWidget;
